import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '@mui/material/styles';
import MaterialToggleButton, { ToggleButtonProps } from '@mui/material/ToggleButton';
import uniqueId from 'lodash/uniqueId';
import { Checkbox, Radio } from '@buoyhealth/common.buoy-input';
import { OptionMedia, ExpandedOptionMedia } from '../Media';

export interface IToggleButtonProps extends Omit<ToggleButtonProps, 'size'> {
  label?: string;
  multiSelect?: boolean;
  hasError?: boolean;
  subText?: string;
  media?: string;
  mediaAltText?: string;
}

const StyledToggleButton = styled(MaterialToggleButton)`
  label {
    color: ${({ theme }) => theme.palette.text.primary};
    text-transform: none;
    pointer-events: none;
    font-size: 18px;
    text-align: left;
    line-height: 1.2;
  }
  label > p {
    font-size: 16px;
    color: ${({ theme }) => theme.palette.gray[50]};
  }

  .MuiCheckbox-root,
  .MuiRadio-root {
    margin-right: 6px;
  }

  &.MuiToggleButton-root {
    justify-content: flex-start;
    margin-bottom: 13px;
    margin-top: 0 !important;
    &:last-child {
      margin-bottom: 0;
    }
    background-color: ${({ theme }) => theme.palette.gray[0]};
    border: 1px solid transparent;
    border-radius: 4px !important;
    padding: 8px 15px;
    transition: 0.2s ease all;

    &:hover {
      background-color: ${({ theme }) => theme.palette.teal[0]};
    }
    &.Mui-focusVisible {
      box-shadow: ${({ theme }) =>
        `0 0 10px 0 ${theme.palette.primary.main}, 0 0 5px 0 ${theme.palette.primary.main}`};
    }
    &.Mui-selected {
      background-color: ${({ theme }) => theme.palette.teal[0]};
      color: ${({ theme }) => theme.palette.primary.main};
      border-color: ${({ theme }) => theme.palette.primary.main};
      &:hover {
        background-color: ${({ theme }) => theme.palette.teal[10]};
      }
      &,
      & + .MuiToggleButton-root.Mui-selected {
        border: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
      }
    }
    &[data-error='true'] {
      border-color: ${({ theme }) => `${theme.palette.status.error} !important`};
    }
  }
`;

/** A selectable button that can display either a checkbox or radio */
export default function ToggleButton({
  hasError,
  label,
  multiSelect,
  value,
  selected,
  subText,
  media,
  mediaAltText,
  ...rest
}: IToggleButtonProps) {
  const theme = useTheme();
  const Control = multiSelect ? Checkbox : Radio;
  const id = uniqueId();
  const checkedIconProps = { color: theme.palette.primary.main };

  const [showExtendedMedia, setShowExtendedMedia] = useState(false);

  const displayExtendedMedia = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation(); // Prevent the toggle button's onClick event from being triggered
    setShowExtendedMedia(!showExtendedMedia);
  };

  const handleCloseExtendedMedia = () => setShowExtendedMedia(false);

  return (
    <>
      <StyledToggleButton
        aria-label={label}
        data-error={!!hasError}
        disableFocusRipple
        id={id}
        selected={selected}
        value={value}
        {...rest}
      >
        <Control
          aria-hidden
          checked={selected}
          checkedIconProps={checkedIconProps}
          disabled
          edge="start"
        />
        <label htmlFor={id}>
          {label}
          {subText && <p className="">{subText}</p>}
        </label>
        {media && (
          <OptionMedia
            media={media}
            mediaAltText={mediaAltText}
            displayExtendedMedia={displayExtendedMedia}
            showExtendedMedia={showExtendedMedia}
          />
        )}
      </StyledToggleButton>
      {media && (
        <ExpandedOptionMedia
          showExtendedMedia={showExtendedMedia}
          handleClose={handleCloseExtendedMedia}
          media={media}
          mediaAltText={mediaAltText}
        />
      )}
    </>
  );
}
